import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext({ open: () => {}, close: () => {} });

export function useLightbox() {
    return useContext(LightboxContext);
}

export default function LightboxProvider({ children }) {
    const [current, setCurrent] = useState(null); // { src, alt }

    const open = useCallback((src, alt = "") => {
        if (!src) return;
        setCurrent({ src, alt });
    }, []);
    const close = useCallback(() => setCurrent(null), []);

    // Lock scroll + esc to close
    useEffect(() => {
        if (!current) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", onKey);
        window.__lenis?.stop();
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            window.__lenis?.start();
            document.body.style.overflow = prevOverflow;
        };
    }, [current, close]);

    return (
        <LightboxContext.Provider value={{ open, close }}>
            {children}
            <AnimatePresence>
                {current && (
                    <motion.div
                        data-testid="lightbox-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Image preview"
                        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm sm:p-8"
                        onClick={close}
                        onContextMenu={(e) => e.preventDefault()}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        <button
                            data-testid="lightbox-close"
                            onClick={close}
                            aria-label="Close preview"
                            className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors duration-200 hover:border-gold hover:text-gold sm:right-8 sm:top-8"
                        >
                            <X size={20} />
                        </button>
                        <motion.img
                            data-testid="lightbox-image"
                            src={current.src}
                            alt={current.alt || "Project image preview"}
                            onClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                            draggable="false"
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.94, opacity: 0 }}
                            transition={{
                                duration: 0.32,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            style={{
                                WebkitUserDrag: "none",
                                WebkitTouchCallout: "none",
                                userSelect: "none",
                            }}
                            className="max-h-[92vh] max-w-[94vw] select-none rounded-xl shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8)]"
                        />
                        <p className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 px-4 text-center text-[10px] font-medium uppercase tracking-[0.3em] text-white/50 sm:bottom-8">
                            Press ESC or tap outside to close
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </LightboxContext.Provider>
    );
}
